import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, CheckCircle2, Lock, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { qualityNcrAPI } from '@/lib/api';
import { MrbCasePhotos } from './ncr/MrbPhotoGallery';
import { StatusBadge, ReasonSelect, useReasons, YesNo, Field, EightDForm, AttachmentsPanel, HistoryList, fmtDate } from './ncr/NcrShared';

const pick = (n) => ({
  reason: n.reason, problem: n.problem, root_cause: n.root_cause, informed_to_customer: n.informed_to_customer,
  immediate_action: n.immediate_action, permanent_action: n.permanent_action, doc_mods: { ...n.doc_mods },
  eight_d_required: !!n.eight_d_required, remarks: n.remarks,
});

const ClosureForm = ({ n, onSaved, formRef }) => {
  const reasons = useReasons();
  const [f, setF] = useState(pick(n));
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(pick(n)), [n]);
  const locked = n.status === 'CLOSED';
  formRef.current = f;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setDoc = (k) => (v) => setF((x) => ({ ...x, doc_mods: { ...x.doc_mods, [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await qualityNcrAPI.saveInternalClosure(n.id, f); toast.success('Closure details saved'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setSaving(false); }
  };
  return (
    <Card className="shadow-none" data-testid="internal-closure-form">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-sm">Closure actions (F/QA/13)</CardTitle>
        <Button size="sm" onClick={save} disabled={saving || locked} data-testid="internal-closure-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save</Button></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Rejection reason<span className="text-red-500"> *</span></Label><ReasonSelect value={f.reason} onChange={set('reason')} reasons={reasons} disabled={locked} testid="internal-reason-select" /></div>
          <Field label="Problem" value={f.problem} onChange={set('problem')} disabled={locked} testid="internal-problem-input" rows={1} />
          <Field label="Root cause" required value={f.root_cause} onChange={set('root_cause')} disabled={locked} testid="internal-root-cause-input" rows={3} />
          <Field label="Whether informed to customer" required value={f.informed_to_customer} onChange={set('informed_to_customer')} disabled={locked} testid="internal-informed-input" rows={3} />
          <Field label="Immediate action" required value={f.immediate_action} onChange={set('immediate_action')} disabled={locked} testid="internal-immediate-input" rows={3} />
          <Field label="Permanent action" required value={f.permanent_action} onChange={set('permanent_action')} disabled={locked} testid="internal-permanent-input" rows={3} />
        </div>
        <div><p className="text-xs font-semibold mb-1">Modification of documents</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <YesNo label="Work instructions" value={f.doc_mods.work_instructions} onChange={setDoc('work_instructions')} disabled={locked} testid="internal-doc-wi" />
            <YesNo label="Control plan" value={f.doc_mods.control_plan} onChange={setDoc('control_plan')} disabled={locked} testid="internal-doc-cp" />
            <YesNo label="Risk file" value={f.doc_mods.risk_file} onChange={setDoc('risk_file')} disabled={locked} testid="internal-doc-risk" />
            <YesNo label="Training records" value={f.doc_mods.training_records} onChange={setDoc('training_records')} disabled={locked} testid="internal-doc-training" />
            <YesNo label="Job cards / checksheets" value={f.doc_mods.job_cards} onChange={setDoc('job_cards')} disabled={locked} testid="internal-doc-jobcards" />
          </div></div>
        <div className="flex items-center gap-2"><Switch checked={f.eight_d_required} onCheckedChange={set('eight_d_required')} disabled={locked} data-testid="internal-8d-required-switch" />
          <Label className="text-xs">8D report required for this rejection</Label></div>
        <div><Label className="text-xs">Remarks</Label><Input value={f.remarks} onChange={(e) => set('remarks')(e.target.value)} disabled={locked} data-testid="internal-remarks-input" /></div>
      </CardContent>
    </Card>
  );
};

export default function InternalNCRDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [n, setN] = useState(null);
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  const load = useCallback(async () => {
    try { setN((await qualityNcrAPI.getInternal(id)).data); } catch { toast.error('NCR not found'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (s) => {
    setBusy(true);
    try {
      if (s !== 'OPEN' && formRef.current && n.status !== 'CLOSED') await qualityNcrAPI.saveInternalClosure(n.id, formRef.current);
      await qualityNcrAPI.setInternalStatus(n.id, s); toast.success(`NCR ${s.toLowerCase()}`); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  if (!n) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const locked = n.status === 'CLOSED';
  return (
    <div className="space-y-4" data-testid="internal-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/quality/internal-ncr')} data-testid="internal-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Register</Button>
        <h1 className="text-xl font-heading font-semibold font-mono" data-testid="internal-number">{n.number}</h1>
        <StatusBadge status={n.status} testid="internal-detail-status" />
        <div className="ml-auto flex gap-2">
          {n.status === 'OPEN' && <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => setStatus('RESOLVED')} data-testid="internal-resolve-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Mark resolved</Button>}
          {n.status !== 'CLOSED' && <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus('CLOSED')} data-testid="internal-close-btn"><Lock className="w-4 h-4 mr-1" />Close</Button>}
          {isAdmin && n.status !== 'OPEN' && <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus('OPEN')} data-testid="internal-reopen-btn"><RotateCcw className="w-4 h-4 mr-1" />Reopen</Button>}
        </div>
      </div>
      <Card className="shadow-none"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-7 gap-3 text-xs">
        {[['Customer', n.customer_name], ['Part', `${n.part_no} ${n.part_name}`], ['Job card', n.job_card_number], ['WO', n.wo_number],
          ['Qty rejected', n.quantity], ['Reported', fmtDate(n.reported_date)], ['Repetitive', n.repetitive]].map(([l, v]) => (
          <div key={l}><p className="text-muted-foreground">{l}</p><p className="font-medium">{v || '—'}</p></div>
        ))}
        {n.mrb_case && <div className="col-span-2 md:col-span-7 border-t pt-2">MRB case: <Link className="text-blue-700 hover:underline" to={`/quality/mrb?case=${n.mrb_case.id}`} data-testid="internal-mrb-link">{n.mrb_case.ncr_number}</Link> · disposition {n.mrb_case.disposition} · {n.mrb_case.qc_remarks}</div>}
      </CardContent></Card>
      <MrbCasePhotos caseId={n.mrb_case_id} />
      <Tabs defaultValue="closure">
        <TabsList>
          <TabsTrigger value="closure" data-testid="internal-tab-closure">Closure</TabsTrigger>
          <TabsTrigger value="8d" data-testid="internal-tab-8d">8D report{n.eight_d_required ? ' *' : ''}</TabsTrigger>
          <TabsTrigger value="files" data-testid="internal-tab-files">Attachments</TabsTrigger>
          <TabsTrigger value="history" data-testid="internal-tab-history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="closure"><ClosureForm n={n} onSaved={load} formRef={formRef} /></TabsContent>
        <TabsContent value="8d"><EightDForm kind="internal" record={n} onSaved={load} disabled={locked}
          defaults={{ tracking_number: n.number, problem_definition: n.problem }} /></TabsContent>
        <TabsContent value="files"><AttachmentsPanel kind="internal" record={n} onChanged={load} disabled={locked} /></TabsContent>
        <TabsContent value="history"><HistoryList items={n.history} /></TabsContent>
      </Tabs>
    </div>
  );
}
